// The tutorial project's content (#1851): what `runTutorialSeed` writes into
// a fresh project, in the user's language.
//
// Versioned with the step script: the steps lean on this shape (the empty last
// scene the core starts in, two episodes of two scenes for the Structure
// chapter, the one planted issue for Visibility). A change a step can notice
// bumps `TUTORIAL_SEED_VERSION`.
//
// Titles and codes of acts, episodes and scenes are not part of the plan: the
// backend renumbers and renames them on every create, so the seed sends only
// the placeholders a create requires. The project's single act
// and its first episode come from the project create itself (`act_quantity`).

import type { TFunction } from 'i18next';
import { Interior, SignCharacter, Time } from '@/enums';
import { buildSceneHeading } from '@/utils/fountain/sceneHeading';

export const TUTORIAL_SEED_VERSION = 1;

export interface TutorialSeedCharacter {
    name: string;
    gender: 'female' | 'male';
    categories: SignCharacter[];
}

export interface TutorialSeedScene {
    /** The scene's body atom (`scene_dialogue`): the heading, a blank line, the
     *  rest. `null` is a scene with no text at all, which the editor opens as
     *  an empty heading. */
    body: string | null;
    /** The location the heading names, which the scene is also tagged with. */
    location: string | null;
    /** The characters who speak in the scene, which it is tagged with. */
    characters: string[];
}

export interface TutorialSeedPlan {
    version: number;
    projectTitle: string;
    characters: TutorialSeedCharacter[];
    /** Root locations, named as the editor names them (upper case). */
    locations: string[];
    /** The act's episodes in order, each its scenes in order. */
    episodes: TutorialSeedScene[][];
}

type CharacterKey = 'heroine' | 'hero';
type LocationKey = 'lighthouse' | 'shore';

const CHARACTERS: { key: CharacterKey; gender: TutorialSeedCharacter['gender']; categories: SignCharacter[] }[] = [
    { key: 'heroine', gender: 'female', categories: [SignCharacter.protagonist] },
    { key: 'hero', gender: 'male', categories: [SignCharacter.ally] },
];

const LOCATIONS: LocationKey[] = ['lighthouse', 'shore'];

interface SceneShape {
    /** `tutorial.seed.scenes.<copy>`: the scene's text below its heading. */
    copy: string;
    interior: Interior;
    location: LocationKey;
    time: Time;
    speakers: CharacterKey[];
}

/** Scene «2-1» carries the planted issue: a hyphen typed for a dash. */
const EPISODES: (SceneShape | null)[][] = [
    [
        { copy: '1-1', interior: Interior.int, location: 'lighthouse', time: Time.night, speakers: ['heroine'] },
        { copy: '1-2', interior: Interior.nat, location: 'shore', time: Time.night, speakers: ['hero'] },
    ],
    [
        { copy: '2-1', interior: Interior.int, location: 'lighthouse', time: Time.day, speakers: ['hero', 'heroine'] },
        { copy: '2-2', interior: Interior.nat, location: 'shore', time: Time.day, speakers: ['heroine'] },
        // The core's practice scene.
        null,
    ],
];

export function buildTutorialSeed(t: TFunction): TutorialSeedPlan {
    const characterName = (key: CharacterKey) => t(`tutorial.seed.characters.${key}`);
    const locationName = (key: LocationKey) => t(`tutorial.seed.locations.${key}`);

    return {
        version: TUTORIAL_SEED_VERSION,
        projectTitle: t('tutorial.seed.project-title'),
        characters: CHARACTERS.map(({ key, gender, categories }) => ({ name: characterName(key), gender, categories })),
        locations: LOCATIONS.map(locationName),
        episodes: EPISODES.map(scenes =>
            scenes.map(shape => {
                if (shape === null) return { body: null, location: null, characters: [] };
                const location = locationName(shape.location);
                const heading = buildSceneHeading(shape.interior, location, '', shape.time);
                return {
                    body: `${heading}\n\n${t(`tutorial.seed.scenes.${shape.copy}`)}`,
                    location,
                    characters: shape.speakers.map(characterName),
                };
            }),
        ),
    };
}
